import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Cpu, Building2, Brain, Palette, Globe, Library, Languages, GraduationCap, Timer } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import SEO from '@/components/university/common/SEO';
import UniversityHeader from '@/components/university/layout/UniversityHeader';
import UniversityFooter from '@/components/university/layout/UniversityFooter';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const M = '#06060c';
const D = '#0a0a14';
const G = '#d4a843';
const IN = '#0ea5e9'; // Updated from lilac to Sky Blue from logo
const BR = 'rgba(255,255,255,0.07)';
const CA = 'rgba(255,255,255,0.04)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const ReligiousCross = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18M7 8h10" />
  </svg>
);

// ─── Data ─────────────────────────────────────────────────────────────────────
const COLLEGES = [
  {
    id: 'tech', icon: <Cpu size={18} />, label: 'university.enrol.col1', defaultLabel: 'Technology & AI',
    degrees: [
      { slug: 'uni-software-development', label: 'BSc Software Development & Engineering', modules: 4, lessons: 14 },
      { slug: 'uni-ai-machine-learning', label: 'BSc Artificial Intelligence & ML', modules: 4, lessons: 11 },
      { slug: 'uni-cybersecurity', label: 'BSc Cybersecurity & Digital Defence', modules: 4, lessons: 10 },
      { slug: 'uni-data-science', label: 'BSc Data Science & Analytics', modules: 4, lessons: 11 },
      { slug: 'uni-cloud-devops', label: 'BSc Cloud Computing & DevOps', modules: 4, lessons: 10 },
    ],
  },
  {
    id: 'biz', icon: <Building2 size={18} />, label: 'university.enrol.col2', defaultLabel: 'Business & Entrepreneurship',
    degrees: [
      { slug: 'uni-business-administration', label: 'BSc Business Administration & Strategy', modules: 4, lessons: 11 },
      { slug: 'uni-digital-marketing', label: 'BSc Digital Marketing & Growth', modules: 5, lessons: 13 },
      { slug: 'uni-finance-investment', label: 'BSc Finance & Investment', modules: 4, lessons: 12 },
      { slug: 'uni-leadership-management', label: 'BSc Leadership & Management', modules: 4, lessons: 11 },
      { slug: 'uni-entrepreneurship-startup', label: 'BSc Entrepreneurship & Startup', modules: 6, lessons: 15 },
    ],
  },
  {
    id: 'health', icon: <Brain size={18} />, label: 'university.enrol.col3', defaultLabel: 'Health & Human Sciences',
    degrees: [
      { slug: 'uni-psychology', label: 'BSc Psychology & Human Behaviour', modules: 5, lessons: 15 },
      { slug: 'uni-nutrition-health', label: 'BSc Nutrition & Health Science', modules: 4, lessons: 12 },
      { slug: 'uni-mental-health-counselling', label: 'BSc Mental Health & Counselling', modules: 4, lessons: 12 },
      { slug: 'uni-health-administration', label: 'BSc Health Administration & Management', modules: 4, lessons: 11 },
      { slug: 'uni-aged-care-gerontology', label: 'BSc Aged Care & Gerontology', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'arts', icon: <Palette size={18} />, label: 'university.enrol.col4', defaultLabel: 'Creative Arts & Media',
    degrees: [
      { slug: 'uni-graphic-design', label: 'BSc Graphic Design & Visual Communication', modules: 4, lessons: 12 },
      { slug: 'uni-creative-writing', label: 'BSc Creative Writing & Storytelling', modules: 4, lessons: 12 },
      { slug: 'uni-film-content-creation', label: 'BSc Film, Video & Content Creation', modules: 4, lessons: 12 },
      { slug: 'uni-music-production', label: 'BSc Music Production & Audio', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'sci', icon: <Globe size={18} />, label: 'university.enrol.col5', defaultLabel: 'Science & Environment',
    degrees: [
      { slug: 'uni-climate-science', label: 'BSc Climate Science & Sustainability', modules: 4, lessons: 12 },
      { slug: 'uni-advanced-biology', label: 'BSc Advanced Biology & Life Sciences', modules: 4, lessons: 12 },
      { slug: 'uni-physics-applied-maths', label: 'BSc Physics & Applied Mathematics', modules: 4, lessons: 12 },
      { slug: 'uni-environmental-science', label: 'BSc Environmental Science & Ecology', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'hum', icon: <Library size={18} />, label: 'university.enrol.col6', defaultLabel: 'Humanities & Philosophy',
    degrees: [
      { slug: 'uni-philosophy-ethics', label: 'BSc Philosophy & Ethics', modules: 4, lessons: 12 },
      { slug: 'uni-world-history', label: 'BSc World History & Civilisations', modules: 4, lessons: 10 },
      { slug: 'uni-sociology', label: 'BSc Sociology & Social Theory', modules: 4, lessons: 12 },
      { slug: 'uni-political-science', label: 'BSc Political Science & Int\'l Relations', modules: 4, lessons: 12 },
      { slug: 'uni-law-legal-literacy', label: 'BSc Law & Legal Literacy', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'lang', icon: <Languages size={18} />, label: 'university.enrol.col7', defaultLabel: 'Languages & Communication',
    degrees: [
      { slug: 'uni-academic-writing', label: 'BSc Academic Writing & Research', modules: 4, lessons: 11 },
      { slug: 'uni-public-speaking', label: 'BSc Public Speaking & Presentation', modules: 4, lessons: 12 },
      { slug: 'uni-linguistics', label: 'BSc Linguistics & Language Science', modules: 4, lessons: 11 },
      { slug: 'uni-cross-cultural-communication', label: 'BSc Cross-Cultural Communication', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'edu', icon: <GraduationCap size={18} />, label: 'university.enrol.col8', defaultLabel: 'Education & Coaching',
    degrees: [
      { slug: 'uni-teaching-instructional-design', label: 'BSc Teaching & Instructional Design', modules: 4, lessons: 11 },
      { slug: 'uni-life-coaching', label: 'BSc Life Coaching & Personal Development', modules: 4, lessons: 12 },
      { slug: 'uni-early-childhood-education', label: 'BSc Early Childhood Education', modules: 4, lessons: 12 },
      { slug: 'uni-corporate-training', label: 'BSc Corporate Training & L&D', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'faith', icon: <ReligiousCross size={18} />, label: 'university.enrol.col9', defaultLabel: 'Faith & Spiritual Studies',
    degrees: [
      { slug: 'uni-biblical-studies-hermeneutics', label: 'BSc Biblical Studies & Hermeneutics', modules: 4, lessons: 12 },
      { slug: 'uni-systematic-theology', label: 'BSc Systematic Theology', modules: 4, lessons: 12 },
      { slug: 'uni-world-religions-comparative-theology', label: 'BSc World Religions & Comparative Theology', modules: 4, lessons: 11 },
      { slug: 'uni-christian-ethics-apologetics', label: 'BSc Christian Ethics & Apologetics', modules: 4, lessons: 12 },
      { slug: 'uni-practical-ministry-pastoral-theology', label: 'BSc Practical Ministry & Pastoral Theology', modules: 4, lessons: 12 },
    ],
  },
  {
    id: 'mastery', icon: <Timer size={18} />, label: 'university.enrol.col10', defaultLabel: 'Personal Mastery',
    degrees: [
      { slug: 'uni-productivity-mastery', label: 'BSc Productivity, Focus & Time Mastery', modules: 4, lessons: 10 },
      { slug: 'uni-financial-literacy', label: 'BSc Financial Literacy for Life', modules: 4, lessons: 10 },
      { slug: 'uni-emotional-intelligence', label: 'BSc Emotional Intelligence & Resilience', modules: 4, lessons: 11 },
      { slug: 'uni-mindfulness-wellbeing', label: 'BSc Mindfulness, Meditation & Wellbeing', modules: 4, lessons: 11 },
    ],
  },
  {
    id: 'prep', icon: <GraduationCap size={18} />, label: 'university.enrol.col11', defaultLabel: 'University Prep',
    degrees: [
      { slug: 'uni-critical-thinking', label: 'BSc Critical Thinking & Logic', modules: 4, lessons: 12 },
      { slug: 'uni-academic-research', label: 'BSc Academic Research & Writing', modules: 4, lessons: 11 },
      { slug: 'uni-career-exploration', label: 'BSc Career Exploration & Skills', modules: 4, lessons: 10 },
      { slug: 'uni-study-strategies', label: 'BSc Advanced Study Strategies', modules: 4, lessons: 12 },
    ],
  },
];

const getPlans = (t: any) => [
  {
    key: 'university_monthly', price: '$29', period: t('university.enrol.planMonthlyPeriod', '/month'),
    title: 'university.enrol.planMonthly', titleDefault: 'Unlimited Monthly',
    desc: 'university.enrol.monthlyDesc', descDefault: 'All 45 degrees. Cancel any time.',
    features: [
      t('university.enrol.monthlyF1', 'All 45 degrees'),
      t('university.enrol.monthlyF3', 'Degree certificates'),
      t('university.enrol.monthlyF4', 'Honours credentials')
    ],
    highlight: true,
  },
  {
    key: 'university_annual', price: '$249', period: t('university.enrol.planAnnualPeriod', '/year'),
    title: 'university.enrol.planAnnual', titleDefault: 'Unlimited Annual',
    desc: 'university.enrol.annualDesc', descDefault: 'Best value — save $189 vs monthly.',
    features: [
      t('university.enrol.annualF1', 'All 45 degrees'),
      t('university.enrol.annualF2', 'All certificates & Honours'),
      t('university.enrol.annualF3', 'Priority new degrees'),
      t('university.enrol.annualF4', 'Save $99/year')
    ],
    highlight: false,
  },
];

export default function UniversityEnrolPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useAuth();

  const collegeParam = searchParams.get('college');
  const [isMobile, setIsMobile] = useState(false);
  const [activeCollege, setActiveCollege] = useState(collegeParam || 'tech');
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'university_monthly');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'browse' | 'plan'>('browse');
  const [expandedDegree, setExpandedDegree] = useState<string | null>(null);
  const [isCollegeDrawerOpen, setIsCollegeDrawerOpen] = useState(false);
  const [selectedDegreeForSyllabus, setSelectedDegreeForSyllabus] = useState<any>(null);

  useEffect(() => {
    if (collegeParam) setActiveCollege(collegeParam);
  }, [collegeParam]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const curriculum = t('university.curriculum.colleges', { returnObjects: true }) as Record<string, any>;

  const college = COLLEGES.find(c => c.id === activeCollege) || COLLEGES[0];

  async function handleCheckout() {
    if (!user) { navigate('/signin?redirect=/university/enrol'); return; }
    setLoading(true);
    try {
      const isAnnual = selectedPlan === 'university_annual';
      const { data } = await supabase.functions.invoke('billing', {
        body: {
          plan_key: isAnnual ? 'university_monthly' : selectedPlan,
          interval: isAnnual ? 'year' : 'month',
          is_facility: false,
          product: 'university'
        },
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  }

  // ─── Shared Components ────────────────────────────────────────────────────────
  const BottomDrawer = ({ isOpen, onClose, title, children }: { isOpen: boolean, onClose: () => void, title: string, children: React.ReactNode }) => (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
              backdropFilter: 'blur(4px)', zIndex: 1000
            }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              position: 'fixed', bottom: 0, left: 0, right: 0,
              background: D, borderTop: `1px solid ${BR}`,
              borderRadius: '24px 24px 0 0', zIndex: 1001,
              maxHeight: '85vh', overflowY: 'auto',
              paddingBottom: 'env(safe-area-inset-bottom, 20px)'
            }}
          >
            <div style={{ padding: '20px 24px', position: 'sticky', top: 0, background: D, borderBottom: `1px solid ${BR}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 2 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h3>
              <button onClick={onClose} style={{ background: CA, border: 'none', color: TX, padding: '8px 16px', borderRadius: 12, fontSize: 13, fontWeight: 700 }}>Done</button>
            </div>
            <div style={{ padding: '24px' }}>
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
    <UniversityHeader/>
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        title="Enrol in Your Degree"
        description="Browse our 10 colleges and 44 degree programmes. Select your study path and enrol in seconds to start learning with your AI professor."
      />

      {/* Header strip */}
      <div style={{ borderBottom: `1px solid ${BR}`, background: D }}>
        <div style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: isMobile ? '16px 20px' : '24px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 16 : 0
        }}>
          <div>
            <span style={{ fontSize: 11, color: G, fontWeight: 700, letterSpacing: '2px' }}>
              {t('university.enrol.eyebrow', 'STILL WATERS UNIVERSITY')}
            </span>
            <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, margin: '2px 0 0', letterSpacing: '-0.3px', color: '#fff' }}>
              {t('university.enrol.title', 'Enrol in a Degree')}
            </h1>
          </div>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: isMobile ? 12 : 16, alignItems: 'center' }}>
            {(['browse', 'plan'] as const).map((s, i) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {i > 0 && <div style={{ width: isMobile ? 12 : 24, height: 1, background: BR }} />}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: step === s ? G : (step === 'plan' && s === 'browse') ? IN : 'rgba(255,255,255,0.08)',
                  fontSize: 12, fontWeight: 700,
                  color: step === s ? '#06060c' : TX,
                  transition: 'all 0.2s',
                }}>
                  {(step === 'plan' && s === 'browse') ? '✓' : i + 1}
                </div>
                {!isMobile && (
                  <span style={{ fontSize: 12, color: step === s ? TX : MU, fontWeight: 600 }}>
                    {s === 'browse'
                      ? t('university.enrol.step1', 'Explore')
                      : t('university.enrol.step2', 'Choose Plan')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '24px 20px 80px' : '48px 24px 80px' }}>
        <AnimatePresence mode="wait">

          {/* ── STEP 1: BROWSE DEGREES ───────────────────────────────────── */}
          {step === 'browse' && (
            <motion.div key="browse" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p style={{ color: MU, fontSize: isMobile ? 14 : 15, marginBottom: isMobile ? 24 : 32, lineHeight: 1.6 }}>
                {t('university.enrol.browseHint', 'Browse 44 degrees across 10 colleges. All included with Unlimited.')}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: isMobile ? 24 : 32 }}>

                {/* College sidebar / Mobile selector */}
                <div>
                  <p style={{ fontSize: 11, color: MU, letterSpacing: '1.5px', fontWeight: 600, marginBottom: 12 }}>
                    {t('university.enrol.colleges', 'COLLEGES')}
                  </p>

                  {isMobile ? (
                    <button
                      onClick={() => setIsCollegeDrawerOpen(true)}
                      style={{
                        width: '100%',
                        background: CA, border: `1px solid ${BR}`,
                        borderRadius: 16, padding: '16px 20px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer', color: TX, transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: IN, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                          {college.icon}
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 700 }}>{t(college.label, college.defaultLabel)}</span>
                      </div>
                      <ChevronDown size={20} color={G} />
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {COLLEGES.map((col) => (
                        <motion.button
                          key={col.id}
                          whileHover={{ x: 3 }}
                          onClick={() => setActiveCollege(col.id)}
                          style={{
                            background: activeCollege === col.id ? 'rgba(212,168,67,0.12)' : 'rgba(255,255,255,0.03)',
                            border: activeCollege === col.id ? '1px solid rgba(212,168,67,0.3)' : '1px solid rgba(255,255,255,0.05)',
                            borderRadius: 12,
                            padding: '12px 14px',
                            cursor: 'pointer', textAlign: 'left',
                            display: 'flex', alignItems: 'center', gap: 12,
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: activeCollege === col.id ? G : 'rgba(255,255,255,0.06)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: activeCollege === col.id ? '#06060c' : TX,
                            flexShrink: 0
                          }}>
                            {col.icon}
                          </div>
                          <span style={{
                            fontSize: 13, fontWeight: 600,
                            color: activeCollege === col.id ? G : TX,
                            lineHeight: 1.2
                          }}>
                            {t(col.label, col.defaultLabel)}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Degree list */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeCollege}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24,
                      padding: isMobile ? '20px' : '20px 24px',
                      background: CA, border: `1px solid ${BR}`, borderRadius: 16,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                    }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: IN, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        {college.icon}
                      </div>
                      <div>
                        <h2 style={{ fontWeight: 800, fontSize: isMobile ? 18 : 22, margin: 0, letterSpacing: '-0.3px', color: '#fff' }}>
                          {t(college.label, college.defaultLabel)}
                        </h2>
                        <p style={{ color: MU, fontSize: 13, margin: '2px 0 0'}}>
                          {college.degrees.length} {t('university.enrol.degrees', 'degree programmes')}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {college.degrees.map((deg, i) => {
                        const currCol = curriculum?.[college.id];
                        const currDeg = currCol?.degrees ? Object.values(currCol.degrees)[i] : null;
                        const modules = (currDeg as any)?.modules || [];
                        const isExpanded = expandedDegree === deg.slug;

                        return (
                          <div key={deg.slug}>
                            <motion.div
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05, duration: 0.3 }}
                              whileHover={isMobile ? {} : { borderColor: 'rgba(212,168,67,0.4)', y: -2 }}
                              onClick={() => {
                                if (isMobile) {
                                  setSelectedDegreeForSyllabus(deg);
                                } else {
                                  setExpandedDegree(isExpanded ? null : deg.slug);
                                }
                              }}
                              style={{
                                background: CA, border: `1px solid ${BR}`,
                                borderRadius: isExpanded ? '16px 16px 0 0' : 16,
                                padding: isMobile ? '16px 20px' : '22px 28px',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                justifyContent: 'space-between',
                                gap: isMobile ? 12 : 16, transition: 'all 0.2s', cursor: 'pointer',
                                position: 'relative'
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <h3 style={{ fontWeight: 700, fontSize: isMobile ? 15 : 17, margin: '0 0 6px', color: '#fff', lineHeight: 1.4 }}>
                                  {(currDeg as any)?.title || deg.label}
                                </h3>
                                <p style={{ color: MU, fontSize: 13, margin: 0, fontWeight: 500 }}>
                                  {modules.length > 0 ? modules.length : deg.modules} {t('university.enrol.modules', 'modules')} · {modules.length > 0 ? modules.reduce((acc: number, m: any) => acc + (m.lessons?.length || 0), 0) : deg.lessons} {t('university.enrol.lessons', 'lessons')}
                                </p>
                              </div>
                              <div style={{
                                display: 'flex',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 12,
                                borderTop: isMobile ? `1px solid ${BR}` : 'none',
                                paddingTop: isMobile ? 12 : 0
                              }}>
                                <span style={{
                                  background: 'rgba(14,165,233,0.08)', color: IN,
                                  border: `1px solid rgba(14,165,233,0.2)`,
                                  borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700,
                                  letterSpacing: '0.5px'
                                }}>
                                  🏆 {t('university.enrol.certificate', 'CERTIFICATE')}
                                </span>
                                {!isMobile && (isExpanded ? <ChevronUp size={20} color={MU} /> : <ChevronDown size={20} color={MU} />)}
                                {isMobile && <ChevronDown size={18} color={MU} style={{ opacity: 0.5 }} />}
                              </div>
                            </motion.div>
                            {!isMobile && (
                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    style={{ overflow: 'hidden' }}
                                  >
                                    <div style={{
                                      padding: '28px',
                                      background: 'rgba(255,255,255,0.015)',
                                      border: `1px solid ${BR}`,
                                      borderTop: 'none',
                                      borderRadius: '0 0 16px 16px',
                                    }}>
                                      <h4 style={{ margin: '0 0 16px', fontSize: 12, color: G, letterSpacing: '1px', fontWeight: 800, textTransform: 'uppercase' }}>
                                        {t('university.enrol.syllabus', 'Module Syllabus')}
                                      </h4>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                        {(() => {
                                          const degData = currCol?.degrees ? Object.values(currCol.degrees)[i] : null;
                                          const modulesList = (degData as any)?.modules || [];
                                          return modulesList.map((m: any, mIdx: number) => (
                                            <div key={mIdx}>
                                              <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                                                <span style={{ fontSize: 12, color: G, fontWeight: 800 }}>{mIdx + 1}</span>
                                                <h5 style={{ margin: 0, fontSize: 15, color: '#fff', fontWeight: 700 }}>{m.title}</h5>
                                              </div>
                                              <ul style={{ margin: 0, paddingLeft: 24, color: MU, fontSize: 14, lineHeight: 1.7 }}>
                                                {m.lessons?.map((l: string, lIdx: number) => (
                                                  <li key={lIdx} style={{ paddingBottom: 6 }}>{l}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          ));
                                        })()}
                                        {(!curriculum?.[activeCollege]?.degrees || Object.keys(curriculum?.[activeCollege]?.degrees).length === 0) && (
                                          <p style={{ margin: 0, color: MU, fontSize: 14, fontStyle: 'italic' }}>{t('university.enrol.detailed_syllabus_coming_soon')}</p>
                                        )}
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {!isMobile && (
                      <motion.button
                        whileHover={{ scale: 1.02, boxShadow: '0 6px 24px rgba(212,168,67,0.3)' }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setStep('plan');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        style={{
                          marginTop: 40, width: '100%',
                          background: `linear-gradient(135deg, ${G}, #b8912e)`,
                          color: '#06060c', border: 'none', borderRadius: 16,
                          padding: '18px', fontSize: 16, fontWeight: 800, cursor: 'pointer',
                          boxShadow: '0 8px 30px rgba(212,168,67,0.25)'
                        }}
                      >
                        {t('university.enrol.choosePlan', 'Choose Your Plan')} →
                      </motion.button>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Mobile Sticky Action Bar */}
              {isMobile && (
                <div style={{
                  position: 'fixed', bottom: 0, left: 0, right: 0,
                  background: 'rgba(13,13,26,0.95)', backdropFilter: 'blur(10px)',
                  borderTop: `1px solid ${BR}`, padding: '16px 20px env(safe-area-inset-bottom, 16px)',
                  zIndex: 100, display: 'flex', flexDirection: 'column', gap: 8
                }}>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setStep('plan');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    style={{
                      width: '100%',
                      background: `linear-gradient(135deg, ${G}, #b8912e)`,
                      color: '#06060c', border: 'none', borderRadius: 12,
                      padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                      boxShadow: '0 8px 30px rgba(212,168,67,0.2)'
                    }}
                  >
                    {t('university.enrol.choosePlan', 'Choose Your Plan')} →
                  </motion.button>
                </div>
              )}

              {/* Drawers */}
              <BottomDrawer
                isOpen={isCollegeDrawerOpen}
                onClose={() => setIsCollegeDrawerOpen(false)}
                title="Select College"
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {COLLEGES.map(col => (
                    <button
                      key={col.id}
                      onClick={() => { setActiveCollege(col.id); setIsCollegeDrawerOpen(false); }}
                      style={{
                        background: activeCollege === col.id ? 'rgba(14,165,233,0.1)' : CA,
                        border: `1px solid ${activeCollege === col.id ? IN : BR}`,
                        borderRadius: 16, padding: '20px 12px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                        cursor: 'pointer', color: TX, textAlign: 'center'
                      }}
                    >
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: activeCollege === col.id ? IN : 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: activeCollege === col.id ? '#fff' : TX }}>
                        {col.icon}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>{t(col.label, col.defaultLabel)}</span>
                    </button>
                  ))}
                </div>
              </BottomDrawer>

              <BottomDrawer
                isOpen={!!selectedDegreeForSyllabus}
                onClose={() => setSelectedDegreeForSyllabus(null)}
                title="Degree Syllabus"
              >
                {selectedDegreeForSyllabus && (
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: 18, color: '#fff', fontWeight: 800 }}>{selectedDegreeForSyllabus.label}</h4>
                    <p style={{ color: G, fontSize: 13, fontWeight: 700, margin: '0 0 24px' }}>
                      {selectedDegreeForSyllabus.modules} modules · {selectedDegreeForSyllabus.lessons} lessons
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                      {(() => {
                        const matchCollege = COLLEGES.find(c => c.id === activeCollege);
                        const dIndex = matchCollege?.degrees.findIndex(d => d.slug === selectedDegreeForSyllabus.slug) ?? 0;
                        const currCol = curriculum?.[activeCollege];
                        const degData = currCol?.degrees ? Object.values(currCol.degrees)[dIndex] : null;
                        const modulesList = (degData as any)?.modules || [];
                        return modulesList.map((m: any, mIdx: number) => (
                          <div key={mIdx}>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                              <span style={{ fontSize: 12, color: G, fontWeight: 800 }}>{mIdx + 1}</span>
                              <h5 style={{ margin: 0, fontSize: 15, color: '#fff', fontWeight: 700 }}>{m.title}</h5>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 24, color: MU, fontSize: 14, lineHeight: 1.7 }}>
                              {m.lessons?.map((l: string, lIdx: number) => (
                                <li key={lIdx} style={{ paddingBottom: 6 }}>{l}</li>
                              ))}
                            </ul>
                          </div>
                        ));
                      })()}
                      {(!curriculum?.[activeCollege]?.degrees || Object.keys(curriculum?.[activeCollege]?.degrees).length === 0) && (
                        <p style={{ margin: 0, color: MU, fontSize: 14, fontStyle: 'italic' }}>{t('university.enrol.detailed_syllabus_coming_soon')}</p>
                      )}
                    </div>
                  </div>
                )}
              </BottomDrawer>
            </motion.div>
          )}

          {/* ── STEP 2: CHOOSE PLAN ──────────────────────────────────────── */}
          {step === 'plan' && (
            <motion.div key="plan" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
                <button
                  onClick={() => setStep('browse')}
                  style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BR}`, color: TX, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 10 }}
                >
                  ← {t('university.enrol.back', 'Back to Study Choice')}
                </button>
              </div>

              <h2 style={{ fontWeight: 800, fontSize: isMobile ? 24 : 32, margin: '0 0 10px', letterSpacing: '-0.5px', color: '#fff' }}>
                {t('university.enrol.planTitle', 'Select your plan')}
              </h2>
              <p style={{ color: MU, fontSize: isMobile ? 15 : 16, marginBottom: 40, lineHeight: 1.6, maxWidth: 600 }}>
                {t('university.enrol.planSub', 'Start immediately. Cancel any time. All plans include AI-taught lessons and verifiable certificates.')}
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? 16 : 24,
                marginBottom: 48
              }}>
                {getPlans(t).map((plan) => (
                  <motion.div
                    key={plan.key}
                    whileHover={isMobile ? {} : { y: -6, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}
                    onClick={() => setSelectedPlan(plan.key)}
                    style={{
                      background: selectedPlan === plan.key
                        ? 'rgba(212,168,67,0.06)'
                        : 'rgba(255,255,255,0.03)',
                      border: selectedPlan === plan.key
                        ? `2px solid ${G}`
                        : `1px solid ${BR}`,
                      borderRadius: 24,
                      padding: isMobile ? '28px 24px' : '36px 32px',
                      cursor: 'pointer', position: 'relative',
                      transition: 'all 0.25s',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {plan.highlight && (
                      <div style={{
                        position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                        background: IN, color: '#fff', borderRadius: 100,
                        padding: '5px 16px', fontSize: 11, fontWeight: 800, letterSpacing: '1px',
                        boxShadow: '0 4px 15px rgba(14,165,233,0.4)',
                        zIndex: 2
                      }}>
                        {t('university.enrol.popular', 'BEST VALUE')}
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                      <p style={{ fontSize: 13, color: selectedPlan === plan.key ? G : MU, fontWeight: 700, margin: 0, letterSpacing: '0.5px' }}>
                        {t(plan.title, plan.titleDefault).toUpperCase()}
                      </p>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        border: `2px solid ${selectedPlan === plan.key ? G : BR}`,
                        background: selectedPlan === plan.key ? G : 'transparent',
                        transition: 'all 0.15s', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selectedPlan === plan.key && (
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#06060c' }} />
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
                      <span style={{ fontSize: 44, fontWeight: 900, color: '#fff', letterSpacing: '-1.5px' }}>
                        {plan.price}
                      </span>
                      <span style={{ fontSize: 15, color: MU, fontWeight: 500 }}>{plan.period}</span>
                    </div>
                    <p style={{ fontSize: 14, color: TX, margin: '0 0 24px', lineHeight: 1.5 }}>
                      {t(plan.desc, plan.descDefault)}
                    </p>

                    <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 24, marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {plan.features.map(f => (
                        <div key={f} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <span style={{ color: G, fontSize: 14, flexShrink: 0, fontWeight: 900 }}>✓</span>
                          <span style={{ fontSize: 14, color: MU, lineHeight: 1.4 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.button
                whileHover={isMobile ? {} : { scale: 1.02, boxShadow: '0 12px 40px rgba(212,168,67,0.4)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCheckout}
                disabled={loading}
                style={{
                  width: '100%', maxWidth: 480, display: 'block', margin: '0 auto',
                  background: `linear-gradient(135deg, ${G}, #b8912e)`,
                  color: '#06060c', border: 'none', borderRadius: 16,
                  padding: '20px', fontSize: 18, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1, boxShadow: '0 8px 32px rgba(212,168,67,0.3)',
                }}
              >
                {loading
                  ? t('university.enrol.processing', 'Processing…')
                  : t('university.enrol.enrolNow', 'Enrol Now')} →
              </motion.button>

              <div style={{ textAlign: 'center', color: MU, fontSize: 13, marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
                <span>🔒 {t('university.enrol.guaranteeSec', 'Secure Checkout')}</span>
                <span>✨ {t('university.enrol.guaranteeAsk', 'Cancel Anytime')}</span>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
    <UniversityFooter />
    </>
  );
}